import { apiClient } from "@/http";
import { apiRoutes } from "./apiRoutes";
import type { PageQuery, PageResp } from "./page";
import type { StorageCleanupTarget } from "./adminStorage";
import type { WorkUnitSummary } from "./adminWorkUnit";
import type { AdminJobTiming, WorkerQueuePage, WorkerQueuePeriod } from "./adminJobTiming";
import type { DatasetUploadJob, DatasetUploadStatus, DatasetUploadFailure } from "./datasetUpload";

export type UploadRecoveryItem = {
  kind: "stale" | "cancelled";
  uuid: string;
  display_name: string;
  status: DatasetUploadStatus;
  dataset_uuid: string | null;
  modified_at: string;
  import_attempts: number | null;
  import_generation: number | null;
  cleanup_status: StorageCleanupTarget["job_status"];
};

/** 멈춘 작업과 취소 Dataset을 변경 시각 최신순으로 페이지 조회한다. */
export async function listUploadRecoveryRows(
  query: PageQuery,
  options: { signal?: AbortSignal } = {},
): Promise<PageResp<UploadRecoveryItem>> {
  return (
    await apiClient.get<PageResp<UploadRecoveryItem>>(apiRoutes.admin.uploadJobsRecovery, {
      params: query,
      signal: options.signal,
    })
  ).data;
}

/**
 * 멈춘 import 를 `uploaded` 로 되돌리고 워커 큐에 다시 넣는다(admin). 워커는 staging 바이트를
 * 재사용한다. 그래서 manifest 확정 전이면 fetching 부터, 확정 뒤이면 보존된 cursor부터 이어간다.
 *
 * 서버는 락 안에서 상태를 다시 검사한다:
 * - 이미 in-flight 가 아니면 409 `upload.reprocess.invalid_status`
 * - Worker lease가 유효하면 409 `upload.reprocess.worker_active`
 * - 작업이 없으면 404 `upload.not_found`
 */
export async function reprocessStaleUploadJob(uploadUuid: string): Promise<DatasetUploadJob> {
  const response = await apiClient.post<DatasetUploadJob>(
    apiRoutes.admin.uploadJobReprocess(uploadUuid),
  );
  return response.data;
}

export type AdminUploadJobQuery = PageQuery & {
  period?: WorkerQueuePeriod;
  status?: readonly DatasetUploadStatus[];
};
export type AdminUploadJob = AdminJobTiming & {
  uuid: string;
  display_name: string;
  status: DatasetUploadStatus;
  import_episode_count: number | null;
  copy_cursor: number;
  commit_cursor: number;
  publish_progress_percent: number | null;
  units: WorkUnitSummary;
  modified_at: string;
};
export type AdminUploadJobDetail = AdminUploadJob & {
  dataset_uuid: string | null;
  failure: DatasetUploadFailure | null;
  import_generation: number;
  import_attempts: number;
  import_file_count: number | null;
  worker_id: string | null;
  worker_lease_expires_at: string | null;
  target_backend: string;
  declared_size_bytes: number;
  staged_file_count: number | null;
  upload_verified: boolean;
  created_at: string;
};
export async function getAdminUploadJob(uuid: string): Promise<AdminUploadJobDetail> {
  return (await apiClient.get<AdminUploadJobDetail>(apiRoutes.admin.uploadJob(uuid))).data;
}

// 업로드 청크의 단계다. 서버가 `copy_cursor`·`commit_cursor` 에서 계산한다.
//   - pending: 복사를 시작하지 않았다.
//   - copying: 복사하는 중이다.
//   - copied: 복사를 마쳤고 확정하지 않았다.
//   - committed: 카탈로그에 확정했다.
export type UploadChunkPhase = "pending" | "copying" | "copied" | "committed";
export type AdminUploadChunk = {
  // 1 부터 시작하는 청크 순서다.
  ordinal: number;
  episode_count: number;
  phase: UploadChunkPhase;
  copied: number;
  committed: number;
};
export type AdminUploadChunks = {
  // 청크 하나의 최대 에피소드 수다(서버 설정).
  chunk_episodes: number;
  items: AdminUploadChunk[];
};

/**
 * 업로드 작업의 청크를 모두 조회한다. 청크는 테이블 행이 아니다. 서버가 커서에서 계산한다.
 * 원본 목록을 만들기 전에는 서버가 에피소드 수를 모른다. 그래서 items 가 비어 있다.
 */
export async function listAdminUploadJobChunks(uuid: string): Promise<AdminUploadChunks> {
  return (await apiClient.get<AdminUploadChunks>(apiRoutes.admin.uploadJobChunks(uuid))).data;
}

export async function listAdminUploadJobs(
  query: AdminUploadJobQuery,
  options: { signal?: AbortSignal } = {},
): Promise<WorkerQueuePage<AdminUploadJob>> {
  return (
    await apiClient.get<WorkerQueuePage<AdminUploadJob>>(apiRoutes.admin.uploadJobs, {
      params: query,
      signal: options.signal,
    })
  ).data;
}
